import React from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";

/**
 * Renders a single organization-defined custom field as a controlled input,
 * driven by its definition. The value is always a string (matching the backend
 * CustomFieldValue contract); for MULTISELECT the string is a JSON array.
 * The parent owns the value map.
 *
 * Reused by Assets now and intended for Procedures (Phase 3).
 *
 * Props:
 *  - definition: { id, label, fieldType, options, required, helpText, placeholder, minValue, maxValue, numberPrecision }
 *  - value: string | undefined
 *  - onChange: (value: string) => void
 *  - error: string | undefined
 */
const DynamicCustomFieldRenderer = ({ definition, value, onChange, error }) => {
  const { t } = useTranslation();
  if (!definition) return null;

  const {
    fieldType, label, required, helpText, options,
    placeholder, minValue, maxValue, numberPrecision,
  } = definition;
  const inputId = `cf-${definition.id}`;
  const invalidClass = error ? "is-invalid" : "";

  // precision → input step: 2 → "0.01", 0 → "1", null → browser default.
  const numberStep =
    numberPrecision == null ? undefined
      : numberPrecision <= 0 ? "1"
      : `0.${"0".repeat(numberPrecision - 1)}1`;

  const renderControl = () => {
    switch (fieldType) {
      case "TEXTAREA":
        return (
          <textarea
            id={inputId}
            className={`form-control ${invalidClass}`}
            value={value || ""}
            maxLength={4096}
            rows={3}
            placeholder={placeholder || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case "NUMBER":
        return (
          <input
            id={inputId}
            type="number"
            className={`form-control ${invalidClass}`}
            value={value || ""}
            min={minValue ?? undefined}
            max={maxValue ?? undefined}
            step={numberStep}
            placeholder={placeholder || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case "DATE":
        return (
          <input
            id={inputId}
            type="date"
            className={`form-control ${invalidClass}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case "DATETIME":
        return (
          <input
            id={inputId}
            type="datetime-local"
            className={`form-control ${invalidClass}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case "SELECT":
        return (
          <select
            id={inputId}
            className={`form-select ${invalidClass}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">{t("shared.select", "Select...")}</option>
            {(options || []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );
      case "MULTISELECT": {
        const opts = (options || []).map((o) => ({ value: o, label: o }));
        let selectedVals = [];
        try {
          selectedVals = value ? JSON.parse(value) : [];
        } catch {
          selectedVals = [];
        }
        const selected = opts.filter((o) => selectedVals.includes(o.value));
        return (
          <Select
            inputId={inputId}
            isMulti
            options={opts}
            value={selected}
            classNamePrefix="cf-multiselect"
            placeholder={placeholder || t("shared.select", "Select...")}
            onChange={(vals) => onChange(JSON.stringify((vals || []).map((v) => v.value)))}
          />
        );
      }
      case "CHECKBOX":
        return (
          <div className="form-check">
            <input
              id={inputId}
              type="checkbox"
              className="form-check-input"
              checked={value === "true"}
              onChange={(e) => onChange(e.target.checked ? "true" : "false")}
            />
          </div>
        );
      case "LINK":
        return (
          <input
            id={inputId}
            type="text"
            className={`form-control ${invalidClass}`}
            value={value || ""}
            placeholder={placeholder || "www.example.com / tel:123-456-7890"}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case "TEXT":
      default:
        return (
          <input
            id={inputId}
            type="text"
            className={`form-control ${invalidClass}`}
            value={value || ""}
            maxLength={4096}
            placeholder={placeholder || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };

  return (
    <div className="mb-3">
      <label htmlFor={inputId} className="form-label">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      {renderControl()}
      {helpText && <small className="text-muted d-block">{helpText}</small>}
      {error && <small className="text-danger d-block">{error}</small>}
    </div>
  );
};

export default DynamicCustomFieldRenderer;
